import {
  Show,
  useRecordContext,
  useCreatePath,
  useRefresh,
  RaRecord,
} from 'react-admin';
import { Link } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { SectionCard, Row, fmtDate } from '../shared/Panel';
import { wallClockDateTime } from '../shared/clientTime';
import { useCompanies } from '../shared/companies';
import {
  REASON_FIELDS,
  SINGLE_VALUE_FIELDS,
  changeFieldLabel,
  changeSourceLabel,
  changeValueLabel,
  isManualPreOrder,
  pointPlace,
} from './preOrderHistory';
import { useFeature } from '../admin/featureFlags/FeatureGate';
import { FEATURE_PRE_ORDER_COPY } from './preOrderCopyApi';
import { PreOrderActions } from './PreOrderActions';
import { ARCHIVED_LOCK_REASON, PreOrderArchiveActions, PreOrderArchivedBanner } from './PreOrderArchive';
import { FEATURE_PRE_ORDER_ARCHIVE, isArchived } from './preOrderArchiveApi';
import {
  extractionStatusLabel,
  preOrderStatusColor,
  preOrderStatusLabel,
  viaWriteStatusColor,
  viaWriteStatusLabel,
  linkRoleLabel,
  linkRoleColor,
} from '../shared/status';
import { PRE_ORDER_FIELDS, FIELD_LABELS, fieldStatus, countReadFields } from '../email/fieldDictionary';
import { PermissionGate, useHasPermission } from '../admin/PermissionGate';
import { ELEMENT } from '../admin/roleCenter/elementKeys';
import { EmailPreview } from '../email/EmailPreview';
import { preOrderReadiness } from './preOrderReadiness';
import { QuestionMarker, fieldLabel, useRequiredFields } from '../shared/requiredFields';
import { IntakeFlowCard, useIntakeFlow } from '../admin/intake/IntakeFlowCard';
import { controlPath } from '../admin/intake/intakeApi';
import { FEATURE_INTAKE_CONTROL_WINDOW } from '../admin/intake/intakeSides';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import { IntakeCancellationPanel } from '../admin/userOffers/IntakeCancellationPanel';
import { PreOrderContractorSection } from './PreOrderContractorSection';
import { PreOrderFuelSurcharge } from '../admin/fuelSurcharges/FuelSurchargeSection';
import { FieldSourceTags, agreementSourceKindLabel, type FieldSource } from './AgreementSource';
import { PreOrderAttachments } from './PreOrderAttachments';
import { ContractTestId } from '../usage/ContractTestId';
import { ClientQuestionsPanel } from '../admin/clientCommunication/ClientQuestionsPanel';
import {
  ShowInSourceLink,
  SourcePreviewProvider,
  SourcePreviewToggle,
  SourceSplit,
  useSourcePreview,
} from './SourcePreview';
import { FEATURE_SOURCE_PREVIEW } from './sourcePreviewApi';
import { extractedFieldsMap } from '../email/fieldDictionary';
import { extractedHint, lowConfidence, sourceLinkFor } from './sourcePreviewRules';
import { FEATURE_PRE_ORDER_DOCUMENTS, isMissing, readSendReadiness } from './preOrderDocumentsApi';
import { PreOrderEmailsCard } from './EmailLinks';
import { PreOrderImporterFields, SendReadinessCard } from './PreOrderSendReadiness';

/**
 * Znacznik przy polu prezlecenia, którego brakuje do zlecenia albo zapisu
 * w TMS (03.11.2026, `preOrderDocuments`) — z listy braków z API.
 */
const MissingTag = ({ record, keys }: { record: RaRecord; keys: readonly string[] }) => {
  const readiness = readSendReadiness(record.sendReadiness);
  if (!readiness || !keys.some((k) => isMissing(readiness, k))) return null;
  return (
    <Chip
      size="small"
      color="warning"
      variant="outlined"
      label="brak — wymagane do zlecenia lub TMS"
      sx={{ ml: 0.75, height: 18, '& .MuiChip-label': { px: 0.75, fontSize: 11 } }}
      data-testid="pre-order-missing-tag"
      data-field={keys[0]}
    />
  );
};

/**
 * Karta prezlecenia: po lewej źródło, po prawej formatka (07.10.2026).
 *
 * Spotkanie wewnętrzne 06.10: „rozbudowa istniejącego ekranu »Prezlecenia«
 * (wzór: treść obok formatki)” — układ dokumentów kierowców z FreightFusion
 * (`driverDocuments`): z jednej strony treść maila albo załącznik, z drugiej
 * dane do uzupełnienia i poprawy. Rozpoznanych danych nie poprawia się na
 * surowej wiadomości, tylko w formatce. Podgląd to ten sam komponent co
 * w skrzynce (`email/EmailPreview.tsx`). CS §11.2: prezlecenie zachowuje dane
 * źródłowe i wskazuje braki — stąd karta „Kompletność i akceptacja” nad
 * formatką. Nowych pól, stanów ani reguł obowiązkowości tu nie ma.
 *
 * Podgląd dla każdego, akcje tylko z przyjęciem zleceń (01.10.2026).
 * Administracja §15: FTL i Trade mają „opcjonalny, filtrowany podgląd
 * rekordów w toku” i „brak edycji danych administracyjnych bez nadanego
 * uprawnienia”. Skrzynka też należy do przyjęcia zleceń, więc bez uprawnienia
 * temat maila jest tekstem, nie odsyłaczem do ekranu, który odpowie 403.
 */

const ManualOriginCard = ({ record }: { record: RaRecord }) => (
  <SectionCard title="Źródło">
    <Typography variant="body2">
      Prezlecenie wpisano ręcznie
      {record.createdBy ? ` (${String(record.createdBy)})` : ''} — nie powstało z maila, więc nie
      ma pól odczytanych automatycznie z maila ani ich pewności. Braki do akceptacji liczy nagłówek.
    </Typography>
  </SectionCard>
);

/**
 * Lewa kolumna: wiadomość, z której powstało prezlecenie. `GET /pre-orders/:id`
 * oddaje ją z treścią i listą załączników; same pliki serwuje skrzynka, więc
 * ich podgląd tylko z przyjęciem zleceń.
 */
const SourcePane = ({ record }: { record: RaRecord }) => {
  const canOpenMailbox = useHasPermission('ORDER_INTAKE');
  const sourceEmail = record.sourceEmail as RaRecord | undefined;
  if (sourceEmail) {
    return <EmailPreview key={String(sourceEmail.id)} record={sourceEmail} attachmentsAllowed={canOpenMailbox} />;
  }
  if (isManualPreOrder(record)) return <ManualOriginCard record={record} />;
  return (
    <SectionCard title="Źródło">
      <Typography variant="body2" color="text.secondary">
        Brak podglądu wiadomości źródłowej.
      </Typography>
    </SectionCard>
  );
};

/**
 * Kompletność i akceptacja — dla każdego, kto ogląda prezlecenie
 * (Administracja §6.4, §15: FTL i Trade sprawdzają, czy zlecenie jest
 * w toku i w jakim stanie, bez telefonu do Administracji). Braki z API.
 */
const ReadinessCard = ({ record }: { record: RaRecord }) => {
  const { missing, accepted, acceptedBy, acceptedAt } = preOrderReadiness(record);
  return (
    <SectionCard title="Kompletność i akceptacja">
      <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }} useFlexGap>
        <Chip size="small" color={preOrderStatusColor(record.status as string)} label={preOrderStatusLabel(record.status as string)} />
        {record.responsibleDept ? <Chip size="small" variant="outlined" label={record.responsibleDept as string} /> : null}
      </Stack>
      {accepted ? (
        <Alert severity="success">
          Zaakceptowane{acceptedBy ? ` przez ${acceptedBy}` : ''}
          {acceptedAt ? `, ${fmtDate(acceptedAt)}` : ''}. Dalsze poprawki robi się na zleceniu, które z niego
          powstało — inaczej nie wiadomo, co właściwie zostało przyjęte.
        </Alert>
      ) : missing.length > 0 ? (
        <Alert severity="warning">Do akceptacji brakuje: {missing.join(', ')}</Alert>
      ) : (
        <Alert severity="info">Komplet pól wymaganych — czeka na akceptację.</Alert>
      )}
    </SectionCard>
  );
};

const confColor = (c: number) => (c >= 0.8 ? 'success' : c >= 0.6 ? 'warning' : 'error');

// —— karta pól AI (checklista present/missing + pewność + źródło) ——
const FieldsCard = ({ record }: { record: RaRecord }) => {
  const extracted = record.extractedFields as Record<string, unknown> | null;
  const { present, total } = countReadFields(extracted);

  const renderField = (field: string) => {
    const s = fieldStatus(field, extracted);
    const content = (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
        {s.present ? (
          <CheckCircleIcon color="success" sx={{ fontSize: 18, mt: '2px' }} />
        ) : (
          <SentimentDissatisfiedIcon color="warning" sx={{ fontSize: 18, mt: '2px' }} />
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" component="span" color={s.present ? 'text.primary' : 'warning.main'}>
            {FIELD_LABELS[field] ?? field}
          </Typography>{' '}
          {s.confidence != null && (
            <Chip size="small" component="span" color={confColor(s.confidence)} label={`${Math.round(s.confidence * 100)}%`} sx={{ height: 16, '& .MuiChip-label': { px: 0.7, fontSize: 10 } }} />
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-word' }}>
            {s.value ?? '—'}
            {/* Podgląd źródła (29.10.2026, `sourcePreview`): brak albo niska pewność — do sprawdzenia w źródle. */}
            {!s.present || lowConfidence(s) ? (
              <ShowInSourceLink hint={extractedHint(extractedFieldsMap(extracted)?.[field] as { source?: unknown })} field={field} />
            ) : null}
          </Typography>
        </Box>
      </Box>
    );
    return s.reason ? (
      <Tooltip key={field} title={s.reason} arrow>
        {content}
      </Tooltip>
    ) : (
      <Box key={field}>{content}</Box>
    );
  };

  return (
    <SectionCard title="Pola odczytane automatycznie z maila">
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1.5, flexWrap: 'wrap' }} useFlexGap>
        <Chip size="small" color={present === total ? 'success' : 'warning'} label={`Pola wymagane: ${present}/${total}`} />
        {record.extractionStatus ? (
          <Chip size="small" variant="outlined" label={`Odczyt: ${extractionStatusLabel(record.extractionStatus as string)}`} />
        ) : null}
        <Typography variant="caption" color="text.secondary">nieczytelne = puste, nie zgadujemy</Typography>
      </Stack>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 1 }}>
        {PRE_ORDER_FIELDS.required.map(renderField)}
      </Box>
      {PRE_ORDER_FIELDS.optional.length > 0 && (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, mb: 0.5 }}>
            Pola opcjonalne
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 1 }}>
            {PRE_ORDER_FIELDS.optional.map(renderField)}
          </Box>
        </>
      )}
    </SectionCard>
  );
};

const OrdersCard = ({ record }: { record: RaRecord }) => {
  const createPath = useCreatePath();
  const orders = (record.orders as RaRecord[] | undefined) ?? [];
  return (
    <SectionCard title={`Zlecenia z tego prezlecenia (${orders.length})`}>
      {orders.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Brak zleceń — prezlecenie nie zostało jeszcze przekształcone w zlecenie.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {orders.map((o) => {
            const writes = (o.viaWrites as RaRecord[] | undefined) ?? [];
            return (
              <Box key={o.id as number} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', borderTop: '1px solid #eee', pt: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 140 }}>
                  {(o.orderNumber as string) || `Zlecenie #${o.id as number}`}
                </Typography>
                <Chip size="small" color={viaWriteStatusColor(o.viaWriteStatus as string)} label={viaWriteStatusLabel(o.viaWriteStatus as string)} />
                {writes.length > 0 && <Chip size="small" variant="outlined" label={`rekordy TMS: ${writes.length}`} />}
                <Button size="small" startIcon={<OpenInNewIcon />} component={Link} to={createPath({ resource: 'orders', type: 'show', id: o.id as number })}>
                  Otwórz
                </Button>
              </Box>
            );
          })}
        </Stack>
      )}
    </SectionCard>
  );
};

// Powiązane wiadomości (N:M): mail tworzący + zmiany + duplikaty.
const LinkedMessagesCard = ({ record }: { record: RaRecord }) => {
  const createPath = useCreatePath();
  const canOpenMailbox = useHasPermission('ORDER_INTAKE');
  const links = (record.emailLinks as RaRecord[] | undefined) ?? [];
  if (links.length === 0) return null;
  return (
    <SectionCard title={`Powiązane wiadomości (${links.length})`}>
      <Stack spacing={1}>
        {links.map((l) => {
          const em = l.email as RaRecord | undefined;
          return (
            <Box key={l.emailId as number} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', borderTop: '1px solid #eee', pt: 1 }}>
              <Chip size="small" color={linkRoleColor(l.role as string)} label={linkRoleLabel(l.role as string)} />
              <Typography variant="body2" sx={{ minWidth: 160 }}>
                {(em?.subject as string) || `Mail #${l.emailId as number}`}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {(em?.fromName as string) ?? ''} · {fmtDate(em?.receivedAt as string)}
              </Typography>
              {canOpenMailbox && (
                <Button size="small" startIcon={<OpenInNewIcon />} component={Link} to={createPath({ resource: 'emails', type: 'show', id: l.emailId as number })}>
                  Otwórz
                </Button>
              )}
            </Box>
          );
        })}
      </Stack>
    </SectionCard>
  );
};

const ChangesCard = ({ record }: { record: RaRecord }) => {
  const companies = useCompanies();
  const changes = (record.changes as RaRecord[] | undefined) ?? [];
  // Nazwy kontrahentów z wpisów „kontrahent z kartoteki” (21.10.2026) — z API.
  const contractorNames = (record.contractorNames as Record<string, string> | undefined) ?? {};
  if (changes.length === 0) return null;
  return (
    <SectionCard title="Historia zmian (audyt)">
      <Table size="small">
        <TableBody>
          {changes.map((c) => (
            <TableRow key={c.id as number}>
              <TableCell sx={{ border: 0 }}>{changeFieldLabel(c.field as string)}</TableCell>
              <TableCell sx={{ border: 0, color: 'text.secondary' }}>
                {REASON_FIELDS.includes(c.field as string)
                  ? `${(c.oldValue as string | null) ?? '—'} — powód: ${(c.newValue as string | null) ?? '—'}`
                  : c.oldValue == null && SINGLE_VALUE_FIELDS.includes(c.field as string)
                  ? changeValueLabel(c.field as string, c.newValue as string | null, companies, contractorNames)
                  : `${changeValueLabel(c.field as string, c.oldValue as string | null, companies, contractorNames)} → ${changeValueLabel(c.field as string, c.newValue as string | null, companies, contractorNames)}`}
              </TableCell>
              <TableCell sx={{ border: 0 }} align="right">
                <Typography variant="caption" color="text.secondary">
                  {[changeSourceLabel(c.source as string | null), c.changedBy as string | null, fmtDate(c.changedAt as string)]
                    .filter(Boolean)
                    .join(' · ')}
                  {c.sourceEmail ? ` · z: ${(c.sourceEmail as RaRecord).subject as string}` : ''}
                  {/* Źródło ustalenia (21.10.2026, `preOrderAgreementSource`). */}
                  {c.agreementSource ? ` · źródło: ${agreementSourceLabel(c.agreementSource as string)}${attachmentName(c)}${c.agreementNote ? ` — ${c.agreementNote as string}` : ''}` : ''}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </SectionCard>
  );
};

/**
 * Skąd skopiowano i jakie kopie powstały (makieta back office v2,
 * BR-ADM-09, 20.10.2026; przełącznik `preOrderCopy`) — odsyłacze.
 */
const CopyRows = ({ record }: { record: RaRecord }) => {
  const createPath = useCreatePath();
  const on = useFeature(FEATURE_PRE_ORDER_COPY);
  if (!on) return null;
  const from = record.copiedFrom as RaRecord | null | undefined;
  const copies = (record.copies as RaRecord[] | undefined) ?? [];
  const link = (id: unknown) => (
    <Link key={String(id)} to={createPath({ resource: 'pre-orders', type: 'show', id: id as number })}>
      #{String(id)}
    </Link>
  );
  return (
    <>
      {from ? <Row label="Skopiowane z">{link(from.id)}</Row> : null}
      {copies.length > 0 ? (
        <Row label="Kopie">
          <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
            {copies.map((c) => link(c.id))}
          </Stack>
        </Row>
      ) : null}
    </>
  );
};

/** Pola wiersza „Załadunek” / „Rozładunek” — do źródła ustalenia (21.10.2026). */
const LOADING_FIELDS = ['loadingCity', 'loadingCountry', 'loadingPostalCode', 'loadingDate'];
const UNLOADING_FIELDS = ['unloadingCity', 'unloadingCountry', 'unloadingPostalCode', 'unloadingDate'];

const agreementSourceLabel = (kind: string): string => agreementSourceKindLabel(kind);

/** Plik od klienta jako źródło wpisu (26.10.2026): „: zam.pdf”. */
const attachmentName = (c: RaRecord): string => {
  const a = c.agreementAttachment as { filename?: string; deletedAt?: string | null } | null | undefined;
  return a?.filename ? `: ${a.filename}${a.deletedAt ? ' (usunięty)' : ''}` : '';
};

/**
 * Zapytanie klienta (26.10.2026, P3): ten sam rekord co prezlecenie. Gdy
 * to wciąż zapytanie — odsyłacz do karty zapytania (tam dostępność, wycena,
 * wynik); gdy prezlecenie powstało z przyjętej oferty — skąd.
 */
export const InquiryOriginBanner = ({ record }: { record: RaRecord }) => {
  const createPath = useCreatePath();
  const inquiry = record.inquiry as { status?: string } | null | undefined;
  if (!inquiry && record.kind !== 'inquiry') return null;
  const link = (
    <Link to={createPath({ resource: 'inquiries', type: 'show', id: record.id as number })}>Z-{String(record.id)}</Link>
  );
  return record.kind === 'inquiry' ? (
    <Alert severity="info" data-testid="pre-order-inquiry-banner">
      To zapytanie klienta {link}, jeszcze nie prezlecenie — dostępność aut, wycena i wynik oferty są na karcie zapytania.
    </Alert>
  ) : (
    <Alert severity="success" data-testid="pre-order-inquiry-origin">
      Prezlecenie powstało z zapytania {link} po przyjęciu oferty przez klienta.
    </Alert>
  );
};

/**
 * „Pliki od klienta” przy podglądzie źródła (29.10.2026): po dołączeniu albo
 * usunięciu pliku odświeża też zakładki źródła.
 */
const SourceAwareAttachments = ({ preOrderId, onChanged }: { preOrderId: number; onChanged: () => void }) => {
  const source = useSourcePreview();
  return (
    <PreOrderAttachments
      preOrderId={preOrderId}
      onChanged={() => {
        onChanged();
        source?.reload();
      }}
    />
  );
};

/** Maile prezlecenia — po powiązaniu albo odpięciu odświeża też zakładki źródła. */
const SourceAwareEmails = ({ preOrderId, onChanged }: { preOrderId: number; onChanged: () => void }) => {
  const source = useSourcePreview();
  return (
    <PreOrderEmailsCard
      preOrderId={preOrderId}
      onChanged={() => {
        onChanged();
        source?.reload();
      }}
    />
  );
};

const PreOrderShowLayout = () => {
  const record = useRecordContext();
  const createPath = useCreatePath();
  const refresh = useRefresh();
  const canOpenMailbox = useHasPermission('ORDER_INTAKE');
  // Obieg CS → Administracja (makieta back office v2, fala B, 21.10.2026,
  // `intakeHandoff`) i okno kontroli (`intakeControlWindow`).
  const { flow, error: flowError, reload: reloadFlow } = useIntakeFlow(record?.id);
  const controlOn = useFeature(FEATURE_INTAKE_CONTROL_WINDOW);
  // Ręczna archiwizacja (25.10.2026, `preOrderArchive`).
  const archiveOn = useFeature(FEATURE_PRE_ORDER_ARCHIVE);
  // Podgląd źródła (29.10.2026, `sourcePreview`, S-29.10-01): źródło pod przyciskiem, obok danych.
  const sourcePreviewOn = useFeature(FEATURE_SOURCE_PREVIEW);
  // Prezlecenia z maili i dokumentów (03.11.2026, `preOrderDocuments`, S-03.11-01).
  const documentsOn = useFeature(FEATURE_PRE_ORDER_DOCUMENTS);
  const miss = (keys: readonly string[]) => (documentsOn && record ? <MissingTag record={record} keys={keys} /> : null);
  // Pola obowiązkowe (24.10.2026, `requiredFieldsHighlight`) i pytanie
  // Administracji przy polu (`intakeQuestionField`) — etykieta i znacznik.
  const isRequired = useRequiredFields();
  const question = flow?.openQuestion ?? null;
  const label = (text: string, keys: readonly string[]) => (
    <>
      {fieldLabel(text, keys.some((k) => isRequired(k)))}
      <QuestionMarker question={question} keys={keys} />
    </>
  );
  if (!record) return null;
  // Źródła ustaleń przy polach (21.10.2026) — API oddaje je tylko z przełącznikiem.
  const sources = record.fieldSources as Record<string, FieldSource> | undefined;
  // Rekord u drugiego działu — edycja i akceptacja tylko u niego (BR-ADM-01).
  const holderLock =
    flow && !flow.mySides.includes(flow.holderSide)
      ? `Prezlecenie jest u działu ${flow.department} — zmiany wprowadza ${flow.department}.`
      : null;
  // Zarchiwizowane — zmiany i akceptacja dopiero po „Przywróć” (API odmawia 409).
  const lockedReason = archiveOn && isArchived(record) ? ARCHIVED_LOCK_REASON : holderLock;

  const company = record.company as RaRecord | undefined;
  const sourceEmail = record.sourceEmail as RaRecord | undefined;
  const price = record.price != null ? `${record.price as number} ${(record.currency as string) ?? ''}`.trim() : '—';
  // „pokaż w źródle” przy polu bez wartości, z niską pewnością albo ustalonym z pliku (29.10.2026).
  const srcLink = (keys: readonly string[]) => {
    const l = sourceLinkFor(record, keys, sources);
    return l.show ? <ShowInSourceLink hint={l.hint} field={keys[0]} /> : null;
  };

  const formSide = (
    <Stack spacing={2} sx={{ minWidth: 0 }} data-testid="pre-order-form-side">
      <PreOrderArchivedBanner record={record} />
      <InquiryOriginBanner record={record} />
      <ReadinessCard record={record} />
      {documentsOn ? <SendReadinessCard readiness={readSendReadiness(record.sendReadiness)} /> : null}

      {/* Anulowanie przez CS i Administrację (25.10.2026, `intakeOrderCancellation`). */}
      <IntakeCancellationPanel
        userOfferId={typeof record.userOfferId === 'number' ? record.userOfferId : null}
      />

      {flow || flowError ? (
        <IntakeFlowCard flow={flow} error={flowError} onChanged={() => void reloadFlow()} />
      ) : null}

      {controlOn ? (
        <PermissionGate permission="ORDER_INTAKE">
          <Box>
            {/* Kontrakt `data-testid` paczki (S-22.10-02): „otwórz okno zlecenia” Administracji. */}
            <ContractTestId id="adm-skrzynka-otworz">
              <Button
                size="small"
                variant="outlined"
                startIcon={<FactCheckIcon />}
                component={Link}
                to={controlPath(record.id as number)}
                data-testid="pre-order-control-window"
              >
                Okno kontroli
              </Button>
            </ContractTestId>
          </Box>
        </PermissionGate>
      ) : null}

      <SectionCard title="Prezlecenie">
        <Table size="small">
          <TableBody>
            {/* Źródło ustalenia przy polu (21.10.2026, `preOrderAgreementSource`) — z API, tylko z przełącznikiem. */}
            <Row label={label('Klient', ['clientName'])}>{(record.clientName as string) ?? '—'}<FieldSourceTags sources={sources} fields={['clientName']} />{srcLink(['clientName'])}{miss(['clientName'])}</Row>
            <Row label={label('Nr zamówienia klienta', ['customerOrderNumber'])}>{(record.customerOrderNumber as string) || '—'}<FieldSourceTags sources={sources} fields={['customerOrderNumber']} />{srcLink(['customerOrderNumber'])}{miss(['customerOrderNumber'])}</Row>
            <Row label="Relacja">{(record.relation as string) ?? '—'}<FieldSourceTags sources={sources} fields={['relation']} /></Row>
            {/* Terminy w czasie polskim — tak jak je wpisano (`shared/clientTime.ts`). */}
            <Row label={label('Załadunek', LOADING_FIELDS)}>{pointPlace(record, 'loading')} · {wallClockDateTime(record.loadingDate as string)}<FieldSourceTags sources={sources} fields={LOADING_FIELDS} />{srcLink(['loadingCity', 'loadingDate'])}{miss(['loadingCity', 'loadingDate'])}</Row>
            <Row label={label('Rozładunek', UNLOADING_FIELDS)}>{pointPlace(record, 'unloading')} · {wallClockDateTime(record.unloadingDate as string)}<FieldSourceTags sources={sources} fields={UNLOADING_FIELDS} />{srcLink(['unloadingCity', 'unloadingDate'])}{miss(['unloadingCity', 'unloadingDate'])}</Row>
            <Row label="Wymagany typ pojazdu">{(record.requiredVehicleType as string) || '—'}<FieldSourceTags sources={sources} fields={['requiredVehicleType']} />{srcLink(['requiredVehicleType'])}</Row>
            <Row label="Towar">{(record.cargo as string) ?? '—'}<FieldSourceTags sources={sources} fields={['cargo']} />{srcLink(['cargo'])}</Row>
            <Row label={label('Waga', ['weight'])}>{record.weight != null ? `${record.weight as number} kg` : '—'}<FieldSourceTags sources={sources} fields={['weight']} />{srcLink(['weight'])}{miss(['weight'])}</Row>
            <Row label="Palety">{(record.pallets as number) ?? '—'}<FieldSourceTags sources={sources} fields={['pallets']} /></Row>
            <Row label={label('Cena', ['price', 'currency'])}>{price}<FieldSourceTags sources={sources} fields={['price', 'currency']} />{srcLink(['price'])}{miss(['price', 'currency'])}</Row>
            {/* Pusty ciąg pokazywał pusty wiersz — brak spółki to kreska. */}
            <Row label={label('Spółka', ['companyId'])}>{(company?.name as string | undefined) || (company?.viaCompanyId as string | undefined) || '—'}{miss(['companyId'])}</Row>
            <CopyRows record={record} />
            <Row label="Źródło">
              {sourceEmail && canOpenMailbox ? (
                <Button size="small" startIcon={<OpenInNewIcon />} component={Link} to={createPath({ resource: 'emails', type: 'show', id: sourceEmail.id as number })}>
                  {(sourceEmail.subject as string) || `Mail #${sourceEmail.id as number}`}
                </Button>
              ) : sourceEmail ? (
                (sourceEmail.subject as string) || `Mail #${sourceEmail.id as number}`
              ) : isManualPreOrder(record) ? (
                'wpisane ręcznie'
              ) : (
                '—'
              )}
            </Row>
          </TableBody>
        </Table>
      </SectionCard>

      {/* Pola pliku dla importera TMS na prezleceniu (03.11.2026, `preOrderDocuments`). */}
      {documentsOn ? <PreOrderImporterFields record={record} onSaved={refresh} /> : null}

      {/* Maile prezlecenia w obie strony (03.11.2026, `preOrderDocuments`). */}
      {documentsOn ? <SourceAwareEmails preOrderId={record.id as number} onChanged={refresh} /> : null}

      {/* Przy podglądzie źródła pliki od klienta stoją przy danych (źródło jest pod przyciskiem). */}
    {sourcePreviewOn ? <SourceAwareAttachments preOrderId={record.id as number} onChanged={refresh} /> : null}

    {/* Kontrahent z kartoteki i podpowiedź VAT (fala B, 21.10.2026, `preOrderContractor`). */}
      <PreOrderContractorSection
        preOrderId={record.id as number}
        clientName={(record.clientName as string | null) ?? null}
        lockedReason={lockedReason}
      />

      <PermissionGate permission="ORDER_INTAKE" element={ELEMENT.preOrderActions}>
        <PreOrderActions showReadiness={false} lockedReason={lockedReason} openQuestion={question} />
      </PermissionGate>

      {/* Pytanie do klienta (26.10.2026, pakiet P1, `clientQuestions`) — szkic
          z braków, wysyłka ręczna, odpowiedź klienta. */}
      <ClientQuestionsPanel preOrderId={record.id as number} />

      {/* Archiwizuj / Przywróć z powodem (25.10.2026, `preOrderArchive`, S-25.10-01). */}
      {archiveOn ? (
        <PermissionGate permission="ORDER_INTAKE">
          <PreOrderArchiveActions record={record} lockedReason={holderLock} />
        </PermissionGate>
      ) : null}

      {/* Korekta paliwowa klienta (makieta back office v2, B4, 21.10.2026) —
          informacja dla kontroli przed zapisem; za przełącznikiem. */}
      <PreOrderFuelSurcharge />

      {!isManualPreOrder(record) && <FieldsCard record={record} />}
      <OrdersCard record={record} />
      {documentsOn ? null : <LinkedMessagesCard record={record} />}
      <ChangesCard record={record} />
    </Stack>
  );

  if (sourcePreviewOn) {
    return (
      <SourcePreviewProvider preOrderId={record.id as number}>
        <Stack spacing={1} sx={{ p: 1, minWidth: 0 }}>
          <Box>
            <SourcePreviewToggle />
          </Box>
          <SourceSplit>{formSide}</SourceSplit>
        </Stack>
      </SourcePreviewProvider>
    );
  }

  return (
    // Dwie kolumny od 1200 px (lg): przy 1366 px z menu po bokach zostaje po
    // ok. 540 px na kolumnę. Węziej — jedna kolumna, źródło nad formatką.
    // `minmax(0, 1fr)` zamiast `1fr`, bo szeroki podgląd rozpychałby kolumnę.
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 1fr) minmax(0, 1fr)' },
        gap: 2,
        p: 1,
        alignItems: 'start',
      }}
    >
      <Stack spacing={2} sx={{ minWidth: 0 }} data-testid="pre-order-source">
        <SourcePane record={record} />
        {/* Pliki od klienta (26.10.2026, `preOrderAttachments`, S-26.10-13) — po zmianie odświeża historię. */}
        <PreOrderAttachments preOrderId={record.id as number} onChanged={refresh} />
      </Stack>

      {formSide}
    </Box>
  );
};

export const PreOrderShow = () => (
  <Show>
    <PreOrderShowLayout />
  </Show>
);
